"use strict";
/* eslint-disable no-use-before-define */
// - The schemas need to be able to reference each other.
const { parse } = require('@whispir/workflow-definition');
const { STEP_TYPES, ACTION_TYPES } = require('@whispir/workflow-factory');
const { variableSchema } = require('./commonSteps/variableSchema');
const { triggerSchema } = require('./triggerSteps/triggerSchema');
const { sendSmsSchema, sendEmailSchema, sendWebformSchema, } = require('./taskSteps/sendMessageSchema');
const { expressionSchema } = require('./conditionSteps/expressionSchema');
const { timeSchema } = require('./conditionSteps/timeSchema');
const { smsResponseCaptureSchema, webformResponseCaptureSchema, } = require('./conditionSteps/responseCaptureSchema');
const { createValidator } = require('./utils/createValidator');
const { array, object, number, string, lazy } = require('./utils/modifiedYup');
/**
  NOTES
  array().required() will fail on empty arrays, therefore don't use it,
  but array().defined() - will make sure that an array exists.
 */
const InputOutputVariablesSchema = object().shape({
    input: array().of(variableSchema),
    output: array().of(variableSchema),
});
/**
  `next` array valdiation:

  rules:

  Valid Start Steps:
    All Triggers
  Valid Body Steps:
    All Tasks
    All Conditions
  Valid Leaf Steps:
    All Tasks
    All Conditions
 */
const baseStepSchema = object().shape({
    id: number().strict().required(),
    variables: InputOutputVariablesSchema.required(),
    next: array()
        .of(lazy(() => baseStepSchema.default(undefined)))
        .defined(),
});
const startStepSchema = baseStepSchema.shape({
    type: string().required().oneOf(['start']),
    data: triggerSchema.required(),
    next: array()
        .of(lazy(() => bodyStep.default(undefined)))
        .required()
        .defined()
        .nullable(),
});
/**
 * Body step = A condition step or a task step - ie. not a root node
 */
const bodyStep = object().shape({
    id: number().strict().required(),
    variables: InputOutputVariablesSchema.required(),
    type: string().required().oneOf([STEP_TYPES.TASK, STEP_TYPES.CONDITION]),
    action: string().required().oneOf([
        // Task Actions
        ACTION_TYPES.SEND_SMS,
        ACTION_TYPES.SEND_EMAIL,
        ACTION_TYPES.SEND_WEBFORM,
        // Condition Actions
        ACTION_TYPES.EXPRESSION,
        ACTION_TYPES.TIME,
        ACTION_TYPES.ON_SMS_REPLY,
        ACTION_TYPES.ON_SMS_NO_REPLY,
        ACTION_TYPES.ON_WEBFORM_REPLY,
        ACTION_TYPES.ON_WEBFORM_NO_REPLY,
    ]),
    data: object()
        // Task Actions
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.TASK && action === ACTION_TYPES.SEND_SMS,
        then: sendSmsSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.TASK && action === ACTION_TYPES.SEND_EMAIL,
        then: sendEmailSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.TASK && action === ACTION_TYPES.SEND_WEBFORM,
        then: sendWebformSchema,
    })
        // Condition Actions
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION && action === ACTION_TYPES.EXPRESSION,
        then: expressionSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION && action === ACTION_TYPES.TIME,
        then: timeSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION && action === ACTION_TYPES.ON_SMS_REPLY,
        then: smsResponseCaptureSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION &&
            action === ACTION_TYPES.ON_SMS_NO_REPLY,
        then: smsResponseCaptureSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION &&
            action === ACTION_TYPES.ON_WEBFORM_REPLY,
        then: webformResponseCaptureSchema,
    })
        .when(['type', 'action'], {
        is: (type, action) => type === STEP_TYPES.CONDITION &&
            action === ACTION_TYPES.ON_WEBFORM_NO_REPLY,
        then: webformResponseCaptureSchema,
    }),
    next: array()
        .nullable()
        .defined()
        .of(lazy(() => bodyStep.default(undefined))),
});
const workflowDefinitionSchema = array().of(startStepSchema).defined().min(1);
const validateWorkflow = createValidator(workflowDefinitionSchema, 'Workflow definition is invalid');
const validateStartStep = createValidator(startStepSchema, 'Invalid start step');
const validateConditionStep = createValidator(bodyStep, 'Invalid condition step');
const validateTaskStep = createValidator(bodyStep, 'Invalid task step');
const validateWorkflowDefinition = (rawDef) => {
    const jsonDef = parse(rawDef);
    return validateWorkflow(jsonDef);
};
module.exports = {
    validateWorkflowDefinition,
    validateStartStep,
    validateConditionStep,
    validateTaskStep,
};
//# sourceMappingURL=workflowSchema.js.map